import React, { ReactNode, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';
import { useRouteError } from 'react-router-dom';

// Google tags and browser extensions call fetch on our pages. Their network
// failures become unhandled rejections in our Sentry project, and we cannot fix them.
const THIRD_PARTY_SCRIPT =
    /googletagmanager\.com|google-analytics\.com|doubleclick\.net|^(chrome|moz|safari(-web)?)-extension:/;
const NETWORK_FAILURE = /Failed to fetch|Load failed|NetworkError/;

export const isThirdPartyNetworkFailure = (event: ErrorEvent): boolean => {
    const exception = event.exception?.values?.[0];
    if (exception?.mechanism?.handled !== false || !NETWORK_FAILURE.test(exception?.value ?? '')) {
        return false;
    }
    const frames = exception?.stacktrace?.frames ?? [];
    if (frames.length > 0) {
        return frames.some((frame) => THIRD_PARTY_SCRIPT.test(frame.filename ?? ''));
    }

    // A cross-origin fetch failure often arrives with no stack frames. Then the last
    // http breadcrumb is the only record of the script that made the call.
    const requests = (event.breadcrumbs ?? []).filter(
        (crumb) => crumb.category === 'fetch' || crumb.category === 'xhr'
    );
    const url = requests[requests.length - 1]?.data?.url;
    return typeof url === 'string' && THIRD_PARTY_SCRIPT.test(url);
};

export const initSentry = (): void => {
    Sentry.init({
        dsn: process.env.SENTRY_DSN,
        environment: process.env.SENTRY_ENV,
        // The New Relic browser agent throws in its own timers, for example in old browsers
        // that ad scanners use. Those errors do not affect our pages.
        denyUrls: [/js-agent\.newrelic\.com/],
        beforeSend: (event) => (isThirdPartyNetworkFailure(event) ? null : event),
    });
};

const ErrorFallback = (): JSX.Element => (
    <div className='app-error-fallback helix-p-4'>
        <p className='app-error-fallback__message helix-body'>
            Something went wrong. Please reload the page.
        </p>
        <button
            type='button'
            className='app-error-fallback__reload-button helix-btn helix-btn--primary'
            onClick={() => window.location.reload()}
        >
            Reload
        </button>
    </div>
);

// Without a boundary, React 17 unmounts the whole app on a render error and the page goes blank.
export const AppErrorBoundary = ({ children }: { children: ReactNode }): JSX.Element => (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>{children}</Sentry.ErrorBoundary>
);

// A data router catches render errors in its routes before AppErrorBoundary can see them.
export const RouteErrorFallback = (): JSX.Element => {
    const error = useRouteError();

    useEffect(() => {
        Sentry.captureException(error);
    }, [error]);

    return <ErrorFallback />;
};
